<script setup lang="ts">
import type { NuxtLinkProps } from '#app';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';

const props = withDefaults(
  defineProps<{
    imageUrl: string | null;
    caption: string | null;
    title: string;
    to: NuxtLinkProps['to'];
    variant?: 'default' | 'ghost';
  }>(),
  { variant: 'default' },
);

/*
  The box ratio is set by the page (230/180 by default, 20/9 in related), so
  only scale down. Sizes run ~20% over the card width: a landscape photo in
  the default box is sized by its height.
*/
const imageSrc = computed(() => resizeImage(props.imageUrl, { width: 360 }));

const imageSrcset = computed(() =>
  resizeImageSrcsetWidths(props.imageUrl, [240, 360, 480, 720, 960]),
);
</script>

<template>
  <NuxtLink
    :to="to"
    :aria-label="title"
    :class="[$style.card, $style[`card_${variant}`]]"
  >
    <div :class="$style.media">
      <img
        v-if="imageUrl"
        :src="imageSrc"
        :srcset="imageSrcset || undefined"
        sizes="(max-width: 479px) 120vw, (max-width: 767px) 60vw, (max-width: 1379px) 40vw, 300px"
        :alt="title"
        :class="$style.media__img"
        loading="lazy"
      />
    </div>

    <div :class="$style.text">
      <span v-if="caption" :class="$style.caption">{{ caption }}</span>
      <h3 :class="$style.title">{{ title }}</h3>
    </div>
  </NuxtLink>
</template>

<style module lang="scss">
.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5x);
  padding: 20px;
  background-color: var(--color-white);
  border-right: 1px solid var(--color-gray-line);
  border-bottom: 1px solid var(--color-gray-line);
  text-decoration: none;
  cursor: pointer;

  &_ghost {
    background-color: transparent;
    border-top: 1px solid var(--color-gray-line);
    border-bottom: none;
  }
}

@media (hover: hover) {
  .card {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

.media {
  width: 100%;
  aspect-ratio: var(--gallery-card-media-ratio, 230 / 180);
  background-color: var(--color-gray-bg);
  overflow: hidden;
}

.media__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.text {
  display: flex;
  flex-direction: column;
  gap: var(--space-1x);
}

.caption {
  @include text_caption2_caps;
  color: var(--color-gray-text);
}

.title {
  @include text_body2;
  @include line_clamp(2);
  color: var(--color-black-1);
  margin: 0;
  font-weight: 400;
}
</style>
